/* EL MOTOR DE REACOMODO (FLIP) · 27-ago-2026
 *
 * 🔴 EL PROBLEMA. Filtrar la rejilla era `p.hidden = true`, y `display:none` no
 * se puede animar: las prendas que no pasan el filtro se BORRAN y las que
 * quedan SALTAN a su nuevo sitio en el mismo cuadro. Con 54 prendas y cuatro
 * columnas, tocar una talla reacomoda media pantalla de golpe. La clienta no
 * ve que su rejilla se reordenó: ve que la página se rompió y se rehízo.
 *
 * 🔴 LA TÉCNICA SE LLAMA FLIP: First, Last, Invert, Play.
 *   1. First  — se apunta dónde está cada prenda ANTES.
 *   2. Last   — se hace el cambio de verdad (esconder, reordenar, lo que sea).
 *   3. Invert — se mide dónde quedó cada una y se le pone un `translate` que la
 *               devuelve visualmente a su sitio viejo, SIN transición.
 *   4. Play   — al cuadro siguiente se le quita ese translate con transición:
 *               la prenda "viaja" de donde estaba a donde le tocó.
 * El navegador nunca anima el layout —que es carísimo—: solo un `translate`,
 * que va en la GPU. Por eso mueve 54 tarjetas sin despeinarse.
 *
 * 🔴 LAS QUE SE VAN NO PUEDEN DESVANECERSE EN SU SITIO. Si se quedan en flujo
 * mientras se apagan, las que sobreviven no pueden ocupar su lugar hasta que
 * terminen: las dos animaciones se estorban y el reacomodo tarda el doble.
 * Se las saca de flujo (`position:absolute`) en las coordenadas exactas donde
 * estaban, así el hueco se cierra YA y ellas se apagan encima. Por eso el
 * contenedor tiene que ser `position: relative` — si no, se van a la esquina
 * de la página.
 *
 * 🔴 TODO VA EN ESTILO EN LÍNEA, a propósito. La rejilla ya trae su propia
 * animación de entrada por scroll (`opacity`, `translate` y un
 * `transition-delay` por columna). Si el reacomodo se escribiera en la hoja
 * habría que pelear especificidad contra eso y el retraso por columna se
 * aplicaría también a los viajes, que se sentiría como lag. El estilo en línea
 * gana siempre y se limpia entero al terminar, dejando la hoja mandando otra
 * vez.
 */
(function () {
  'use strict';

  var DUR_VIAJE = 420;   /* lo que tarda una prenda en llegar a su nuevo sitio */
  var DUR_SALE  = 160;   /* lo que tarda en apagarse la que se va */
  var CURVA     = 'cubic-bezier(0.32, 0.72, 0, 1)';

  function quieto() {
    return window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* Deja el elemento como estaba: sin esto, una prenda que viajó se queda con
     un `transition` en línea y la siguiente animación de scroll sale rara. */
  function limpia(el) {
    el.style.transition = '';
    el.style.translate = '';
    el.style.position = '';
    el.style.left = '';
    el.style.top = '';
    el.style.width = '';
    el.style.height = '';
    el.style.margin = '';
    el.style.opacity = '';
    el.style.scale = '';
    el.style.pointerEvents = '';
    el.style.zIndex = '';
  }

  /* Si la clienta toca dos chips seguidos, la corrida anterior sigue a medias:
     hay prendas fuera de flujo apagándose. Se rematan antes de medir, o la
     medición del "antes" incluye fantasmas y todo el reacomodo sale torcido. */
  function rematarSalientes(caja) {
    [].slice.call(caja.querySelectorAll('[data-sale]')).forEach(function (el) {
      el.removeAttribute('data-sale');
      limpia(el);
      el.hidden = true;
    });
  }

  /* `cambia` es la mutación real del DOM (esconder, reordenar). Este motor no
     sabe ni le importa qué hace: solo mide antes y después. Por eso sirve igual
     para la rejilla del catálogo que para las líneas de la bolsa. */
  function reacomoda(caja, elementos, cambia) {
    if (!caja || quieto()) { cambia(); return; }

    rematarSalientes(caja);

    /* ── 1. FIRST ── */
    var antes = [];
    elementos.forEach(function (el) {
      if (!el.hidden) antes.push({ el: el, r: el.getBoundingClientRect() });
    });
    var altoAntes = caja.getBoundingClientRect().height;
    /* ── 2. LAST ── */
    cambia();

    /* 🔴 EL ORIGEN SE MIDE DESPUÉS DEL CAMBIO, Y HAY QUE CONTAR EL RELLENO.
       Dos errores seguidos costaron esto, los dos medidos el 27-ago:

       1. `getBoundingClientRect()` da la caja de BORDE, pero un
          `position:absolute` se coloca contra la caja de RELLENO del ancestro.
          Sin restar el `padding-top` del muro, las prendas que se iban salían
          88px más abajo.
       2. Y hay que medirlo DESPUÉS de `cambia()`, no antes. Filtrar enciende el
          renglón de "recordamos tu talla", que empuja la rejilla 40px hacia
          abajo. Con el origen viejo quedaban 40px corridas.

       Las que se van tienen que quedarse en sus coordenadas de PANTALLA
       viejas; para eso el punto de referencia es dónde está el contenedor
       AHORA, no dónde estaba. */
    var cajaRect = caja.getBoundingClientRect();
    var cs = getComputedStyle(caja);
    var origenX = cajaRect.left + (parseFloat(cs.borderLeftWidth) || 0) + (parseFloat(cs.paddingLeft) || 0);
    var origenY = cajaRect.top + (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.paddingTop) || 0);

    /* Las que acaban de esconderse vuelven a pantalla, fuera de flujo, justo
       donde estaban. `margin: 0` porque `left/top` ya son la posición final:
       dejarle su margen la correría otra vez. */
    var salen = [];
    antes.forEach(function (a) {
      if (!a.el.hidden) return;
      a.el.hidden = false;
      a.el.setAttribute('data-sale', '');
      a.el.style.position = 'absolute';
      a.el.style.margin = '0';
      a.el.style.left = (a.r.left - origenX) + 'px';
      a.el.style.top = (a.r.top - origenY) + 'px';
      a.el.style.width = a.r.width + 'px';
      a.el.style.height = a.r.height + 'px';
      a.el.style.pointerEvents = 'none';
      a.el.style.zIndex = '0';
      a.el.style.opacity = '1';
      a.el.style.transition = 'opacity ' + DUR_SALE + 'ms linear, scale ' +
                              DUR_SALE + 'ms ' + CURVA;
      salen.push(a.el);
    });

    /* ── 3. INVERT ── (medir aquí fuerza el cálculo de la nueva rejilla) */
    var viajan = [];
    antes.forEach(function (a) {
      if (a.el.hasAttribute('data-sale')) return;
      var n = a.el.getBoundingClientRect();
      var dx = a.r.left - n.left;
      var dy = a.r.top - n.top;
      /* Media docena de prendas no se mueven ni un píxel en un filtro típico.
         Ponerles transición igual es trabajo de GPU regalado. */
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
      a.el.style.transition = 'none';
      a.el.style.translate = dx + 'px ' + dy + 'px';
      viajan.push(a.el);
    });

    /* 🔴 Y EL CONTENEDOR TAMBIÉN TIENE QUE VIAJAR. Sin esto las prendas se
       deslizan bonito durante 420ms mientras el ALTO de la rejilla cambia en el
       primer cuadro: al filtrar 11 de 54 en cuatro columnas se pierden tres
       renglones de golpe y todo lo que va debajo —el pie de página incluido—
       pega un salto hacia arriba. Se anima el alto en paralelo, con la misma
       duración y la misma curva, para que la página baje al mismo ritmo al que
       se acomodan las prendas. Se le fija el alto viejo y se suelta al nuevo. */
    var altoDespues = caja.getBoundingClientRect().height;
    var animaAlto = Math.abs(altoDespues - altoAntes) > 1;
    if (animaAlto) {
      caja.style.transition = 'none';
      caja.style.height = altoAntes + 'px';
      caja.style.overflow = 'clip';   /* que un renglón de más no asome fuera */
    }

    /* ── 4. PLAY ── dos cuadros: uno para que el navegador se quede con la
       posición invertida como estado inicial, otro para soltarla. */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        viajan.forEach(function (el) {
          el.style.transition = 'translate ' + DUR_VIAJE + 'ms ' + CURVA;
          el.style.translate = '0px 0px';
        });
        salen.forEach(function (el) { el.style.opacity = '0'; el.style.scale = '0.97'; });
        if (animaAlto) {
          caja.style.transition = 'height ' + DUR_VIAJE + 'ms ' + CURVA;
          caja.style.height = altoDespues + 'px';
        }
      });
    });

    /* El alto se suelta al terminar: dejarlo fijo rompería la rejilla en cuanto
       cambie el ancho de la ventana o entren prendas por scroll infinito. */
    if (animaAlto) setTimeout(function () {
      caja.style.transition = '';
      caja.style.height = '';
      caja.style.overflow = '';
    }, DUR_VIAJE + 60);

    /* La limpieza va por reloj y no por `transitionend`: son decenas de
       elementos, el evento de cada uno llega por su lado y basta con que uno se
       pierda para dejar estilos en línea colgados. Un solo reloj al final es
       más simple y no se puede perder. Las que se van se rematan antes que las
       que viajan porque su animación es más corta. */
    setTimeout(function () { rematarSalientes(caja); }, DUR_SALE + 60);
    setTimeout(function () { viajan.forEach(limpia); }, DUR_VIAJE + 60);
  }

  window.AvoraFlip = { reacomoda: reacomoda };
})();
